import { Alert, Button, Input, Table, Select, message, Modal, Tag } from "antd";
import DatePicker from "../../../../../components/inputs/MomentDatePicker";
import axios from "axios";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { serverUrl } from "../../../../../nestserver";

const StudentFeDetails = ({
  classId,
  feeResponse,
  setFeeResponse,
  isViewStudentFee,
  studentId,
  fetchStudentFee,
  isParent,
  refreshKey,
}) => {
  const [editedValues, setEditedValues] = useState({});
  const [editedStudentFeeValues, setEditedStudentFeeValues] = useState({});
  const [editingStudentFeeRow, setEditingStudentFeeRow] = useState({});
  const [isEditMode] = useState(false);
  const [editingKey, setEditingKey] = useState("");
  const [editableStudentFee, setEditableStudentFee] = useState([]);
  const [studentFee, setStudentFee] = useState();

  const [isDiscountModalVisible, setIsDiscountModalVisible] = useState(false);
  const [modalRecordId, setModalRecordId] = useState(null);
  const [modalDiscountAmount, setModalDiscountAmount] = useState("");
  const [modalDiscountReason, setModalDiscountReason] = useState("");
  const [modalDiscountTermIndices, setModalDiscountTermIndices] = useState([]);
  const [modalInstallments, setModalInstallments] = useState([]);
  const [modalFeeType, setModalFeeType] = useState(null);

  const [isMiscDiscountModalVisible, setIsMiscDiscountModalVisible] =
    useState(false);
  const [miscDiscountAmount, setMiscDiscountAmount] = useState("");
  const [miscDiscountReason, setMiscDiscountReason] = useState("");
  const [miscTuitionRecordId, setMiscTuitionRecordId] = useState(null);
  const [, setMiscCurrentDiscount] = useState(0);
  const [miscEditMode, setMiscEditMode] = useState(false);

  const handleOpenDiscountModal = (record, editedRecord) => {
    setModalRecordId(record._id);
    setModalFeeType(record?.fee_template?.type?.toLowerCase());
    setModalDiscountAmount(
      editedRecord?.discountAmount !== undefined
        ? editedRecord.discountAmount
        : record.discountStatus === "pending"
          ? record.tempDiscountAmount || 0
          : record.discount || 0,
    );
    setModalDiscountReason(
      editedRecord?.discountReason !== undefined
        ? editedRecord.discountReason
        : record.discountStatus === "pending"
          ? record.tempDiscountReason || ""
          : record.reason || "",
    );

    const installments = record?.installment_list || [];
    const isTuition = record?.fee_template?.type?.toLowerCase() === "tution";
    const templateInstallments =
      record?.fee_template?.installment_list || installments;

    const effectiveInstallments = isTuition
      ? templateInstallments
      : installments.length > 0
        ? installments
        : templateInstallments;
    setModalInstallments(effectiveInstallments);

    let initialIndices = [];
    if (
      editedRecord?.discountTermIndices !== undefined &&
      editedRecord?.discountTermIndices !== null
    ) {
      initialIndices = editedRecord.discountTermIndices;
    } else {
      initialIndices =
        record.discountStatus === "pending"
          ? record.tempDiscountTermIndices
          : record.discountTermIndices;
    }

    // Only filter out "other fee" for tuition type
    let eligibleInstallments = effectiveInstallments;
    if (isTuition) {
      const otherFeeIdx = effectiveInstallments.findIndex(
        (inst) => inst.name?.toLowerCase() === "other fee",
      );
      if (initialIndices && otherFeeIdx !== -1) {
        initialIndices = initialIndices.filter((idx) => idx !== otherFeeIdx);
      }
      eligibleInstallments = effectiveInstallments.filter(
        (inst) => inst.name?.toLowerCase() !== "other fee",
      );
    }

    if (
      initialIndices &&
      initialIndices.length === eligibleInstallments.length
    ) {
      setModalDiscountTermIndices(["all"]);
    } else if (initialIndices && initialIndices.length > 0) {
      setModalDiscountTermIndices(initialIndices);
    } else {
      setModalDiscountTermIndices(["all"]);
    }
    setIsDiscountModalVisible(true);
  };

  const handleOpenMiscDiscountModal = () => {
    const tuitionRecord = studentFee?.find(
      (sf) => sf?.fee_template?.type?.toLowerCase() === "tution",
    );
    if (!tuitionRecord) return;
    const otherFeeInst = tuitionRecord?.installment_list?.find(
      (i) => i.name?.toLowerCase() === "other fee",
    );
    if (!otherFeeInst) return;

    setMiscTuitionRecordId(tuitionRecord._id);
    setMiscCurrentDiscount(Number(otherFeeInst?.discount || 0));
    setMiscDiscountAmount(
      otherFeeInst?.discount ? String(otherFeeInst.discount) : "",
    );
    setMiscDiscountReason(otherFeeInst?.miscDiscountReason || "");
    setIsMiscDiscountModalVisible(true);
  };

  const handleSaveMiscDiscount = async () => {
    const amount = Number(miscDiscountAmount || 0);

    // Block if a tuition discount is already pending (backend shares the same temp fields)
    const tuitionRec = studentFee?.find(
      (sf) => sf?.fee_template?.type?.toLowerCase() === "tution",
    );
    if (
      tuitionRec?.discountStatus === "pending" &&
      !tuitionRec?.tempMiscFeeType
    ) {
      message.error(
        "A tuition fee discount is currently pending approval. Please resolve it before applying a miscellaneous fee discount.",
      );
      return;
    }

    if (amount > 0 && !miscDiscountReason.trim()) {
      message.error("A reason is required to apply the discount.");
      return;
    }

    // Requirement 4: discount must not exceed the remaining misc fee balance
    const otherFeeInst = tuitionRec?.installment_list?.find(
      (i) => i.name?.toLowerCase() === "other fee",
    );
    const otherFeeRemaining = Number(otherFeeInst?.remaining || 0);
    if (amount > 0 && amount > otherFeeRemaining) {
      message.error(
        `Discount (₹${amount.toLocaleString()}) cannot exceed the remaining miscellaneous fee balance (₹${otherFeeRemaining.toLocaleString()}).`,
      );
      return;
    }

    try {
      await axios.patch(
        `${serverUrl}/admin/student-fee/${miscTuitionRecordId}`,
        {
          miscDiscountAmount: amount,
          miscDiscountReason: miscDiscountReason.trim(),
          // Requirement 3: discount applies to the total — no specific fee type
          miscFeeType: null,
        },
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        },
      );
      message.success("Miscellaneous Fees discount applied successfully.");
      setIsMiscDiscountModalVisible(false);
      await fetchStudentFees();
      if (fetchStudentFee) await fetchStudentFee();
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to apply misc discount.",
      );
    }
  };

  const handleSelectChange = (value) => {
    if (value.includes("all")) {
      if (!modalDiscountTermIndices.includes("all")) {
        setModalDiscountTermIndices(["all"]);
      } else if (value.length > 1) {
        setModalDiscountTermIndices(value.filter((val) => val !== "all"));
      } else {
        setModalDiscountTermIndices(["all"]);
      }
    } else {
      setModalDiscountTermIndices(value);
    }
  };

  const handleSaveDiscount = () => {
    const amount = Number(modalDiscountAmount || 0);
    const reason = modalDiscountReason || "";

    if (amount > 0 && !reason.trim()) {
      message.error("A reason is required to apply the discount.");
      return;
    }

    if (
      amount > 0 &&
      (!modalDiscountTermIndices || modalDiscountTermIndices.length === 0)
    ) {
      message.error("Please select at least one term to apply the discount.");
      return;
    }

    const isTuitionModal = modalFeeType === "tution";

    const getTermAmount = (inst) => {
      if (inst && inst.payable_components) {
        const comp = Array.isArray(inst.payable_components)
          ? inst.payable_components[0]
          : inst.payable_components;
        if (comp) {
          if (isTuitionModal) {
            // For tuition: use the tution key or first numeric key
            let adjKey = comp.hasOwnProperty("tution") ? "tution" : null;
            if (!adjKey) {
              adjKey = Object.keys(comp).find(
                (k) => k !== "total_amount" && !isNaN(parseFloat(comp[k])),
              );
            }
            return adjKey ? Number(comp[adjKey] || 0) : 0;
          } else {
            // For transportation/others: use total_amount of each installment
            return Number(comp.total_amount || 0);
          }
        }
      }
      return 0;
    };

    const activeIndices = modalDiscountTermIndices.includes("all")
      ? modalInstallments
          .map((inst, idx) =>
            isTuitionModal && inst.name?.toLowerCase() === "other fee"
              ? -1
              : idx,
          )
          .filter((idx) => idx !== -1)
      : modalDiscountTermIndices.map(Number);

    const selectedInstallments = modalInstallments.filter((_, idx) =>
      activeIndices.includes(idx),
    );
    const totalAvailableAmount = selectedInstallments.reduce(
      (sum, inst) => sum + getTermAmount(inst),
      0,
    );

    if (amount > totalAvailableAmount) {
      message.error(
        `Discount amount (₹${amount.toLocaleString()}) cannot exceed the fee amount of the selected terms (₹${totalAvailableAmount.toLocaleString()}).`,
      );
      return;
    }

    const newStudentFee = editableStudentFee.map((item) =>
      item._id === modalRecordId
        ? {
            ...item,
            discountAmount: amount,
            discountReason: reason.trim(),
            discountTermIndices: activeIndices,
          }
        : item,
    );
    setEditableStudentFee(newStudentFee);
    setIsDiscountModalVisible(false);
  };

  const feeColumn = [
    {
      title: "Name",
      key: "",
      align: "center",
      render: (record) => {
        const type = record?.fee_template?.type;
        const displayType =
          type?.toLowerCase() === "tution"
            ? "Tuition"
            : type?.toLowerCase() === "daycare"
              ? "Day Care"
              : type?.toLowerCase() === "other fee"
                ? "Miscellaneous Fees"
                : type;
        return (
          <div
            key={record?._id}
            style={{ color: "#111111" }}
            className=" text-base font-semibold text-center capitalize"
          >
            {displayType}
          </div>
        );
      },
    },
    {
      title: "Total Amount",
      dataIndex: [],
      key: "",
      align: "center",
      render: (record) => {
        let displayedTotal;
        if (record?.fee_template?.type?.toLowerCase() === "tution") {
          displayedTotal = (record?.installment_list || [])
            .filter((inst) => inst.name?.toLowerCase() !== "other fee")
            .reduce((sum, inst) => {
              const comp =
                inst.payable_components?.[0] ||
                inst.payable_components ||
                {};
              return sum + (Number(comp.total_amount) || 0);
            }, 0);
        } else {
          displayedTotal =
            Number(record?.total || 0) +
            Number(record?.discount || 0) -
            Number(record?.additional || 0);
        }
        return (
          <div style={{ color: "#262626" }} className="text-sm text-center">
            {displayedTotal}
          </div>
        );
      },
    },
    {
      title: "Paid Amount",
      dataIndex: [],
      key: "",
      align: "center",
      render: (record) => {
        const otherFeeInst = record?.installment_list?.find(
          (inst) => inst.name?.toLowerCase() === "other fee",
        );
        const otherPaid = otherFeeInst?.paid || 0;
        const displayedPaid =
          record?.fee_template?.type?.toLowerCase() === "tution"
            ? (record?.paid || 0) - otherPaid
            : record?.paid || 0;

        let pendingPaymentAmount = 0;
        if (record?.__isMiscRow) {
          const tuitionRec = studentFee?.find(
            (sf) => sf?.fee_template?.type?.toLowerCase() === "tution"
          );
          const pendingMiscTxs = tuitionRec?.pendingTransactions?.filter(
            (tx) => tx.feeCategory === "other"
          ) || [];
          pendingPaymentAmount = pendingMiscTxs.reduce((sum, tx) => sum + (tx.amount || 0), 0);
        } else {
          const pendingTxs = record?.pendingTransactions?.filter(
            (tx) => tx.feeCategory === "tuition"
          ) || [];
          pendingPaymentAmount = pendingTxs.reduce((sum, tx) => sum + (tx.amount || 0), 0);
        }

        return (
          <div className="flex flex-col gap-1 justify-center items-center text-center">
            <div
              style={{
                color: "#262626",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              className="text-sm"
            >
              {displayedPaid}
            </div>
            {pendingPaymentAmount > 0 && (
              <Tag
                color="orange"
                className="font-semibold text-center m-0 w-fit mx-auto"
              >
                Pending: ₹{pendingPaymentAmount.toLocaleString()}
              </Tag>
            )}
          </div>
        );
      },
    },

    {
      title: "Remaining Amount",
      dataIndex: [],
      key: "",
      align: "center",
      render: (record) => {
        const otherFeeInst = record?.installment_list?.find(
          (inst) => inst.name?.toLowerCase() === "other fee",
        );
        const otherRemaining = otherFeeInst?.remaining || 0;
        const displayedRemaining =
          record?.fee_template?.type?.toLowerCase() === "tution"
            ? (record?.remaining || 0) - otherRemaining
            : record?.remaining || 0;
        return (
          <div
            style={{
              color: "#262626",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            className="text-sm"
          >
            {displayedRemaining}
          </div>
        );
      },
    },
    {
      title: "Discount / Additional",
      dataIndex: [],
      key: "discount_additional",
      align: "center",
      render: (_, record) => {
        if (record?.__isMiscRow) {
          const miscDisc = Number(record?.miscDiscount || 0);
          const isPending = record?.discountStatus === "pending";
          const pendingAmount = Number(record?.tempDiscountAmount) || 0;

          if (!isParent && miscEditMode) {
            return (
              <div className="flex flex-col gap-1 items-center text-center">
                {miscDisc > 0 && (
                  <div
                    style={{ color: "#262626" }}
                    className="text-sm font-semibold"
                  >
                    {miscDisc.toLocaleString()}
                  </div>
                )}
                {isPending && (
                  <Tag
                    color="orange"
                    className="font-semibold text-center m-0 w-fit mx-auto"
                  >
                    Pending: ₹{pendingAmount.toLocaleString()}
                  </Tag>
                )}
                {miscDisc > 0 ? (
                  <Tag color="green" style={{ margin: 0 }}>
                    Approved
                  </Tag>
                ) : (
                  <Button
                    type="dashed"
                    size="small"
                    onClick={handleOpenMiscDiscountModal}
                  >
                    {isPending ? "Edit Discount" : "Add Discount"}
                  </Button>
                )}
              </div>
            );
          }

          return (
            <div className="flex flex-col gap-1 justify-center items-center text-center">
              {miscDisc > 0 && (
                <div
                  style={{ color: "#262626" }}
                  className="text-sm font-semibold"
                >
                  {miscDisc.toLocaleString()}
                </div>
              )}
              {isPending && (
                <Tag
                  color="orange"
                  className="font-semibold text-center m-0 w-fit mx-auto"
                >
                  Pending: ₹{pendingAmount.toLocaleString()}
                </Tag>
              )}
              {miscDisc > 0 && !isPending && (
                <Tag color="green" style={{ margin: 0 }}>
                  Approved
                </Tag>
              )}
              {!miscDisc && !isPending && (
                <div style={{ color: "#262626" }} className="text-sm">
                  -
                </div>
              )}
            </div>
          );
        }

        const isEditing = editingKey === record._id;

        if (isEditing) {
          // Discount is locked once approved — no further edits allowed.
          // Only lock when the record's own discount > 0; a misc-fee-only
          // approval sets discountStatus="approved" on the tuition record but
          // leaves discount=0, so we must still allow a tuition discount.
          if (record.discountStatus === "approved") {
            const approvedDiscount = Number(record.discount || 0);
            if (approvedDiscount > 0) {
              return (
                <div className="flex flex-col gap-1 items-center text-center">
                  <div style={{ color: "#262626" }} className="text-sm font-semibold">
                    {approvedDiscount.toLocaleString()}
                  </div>
                  <Tag color="green" style={{ margin: 0 }}>
                    Approved
                  </Tag>
                </div>
              );
            }
          }

          const editedRecord = editableStudentFee.find(
            (item) => item._id === record._id,
          );
          const currentDiscount = Number(editedRecord?.discountAmount || 0);

          if (currentDiscount > 0) {
            return (
              <div className="flex flex-col gap-1 items-center text-center">
                <div
                  style={{ color: "#262626" }}
                  className="text-sm font-semibold"
                >
                  {currentDiscount.toLocaleString()}
                </div>
                <Button
                  type="link"
                  size="small"
                  onClick={() => handleOpenDiscountModal(record, editedRecord)}
                  style={{ padding: 0, textAlign: "center" }}
                >
                  Edit Discount
                </Button>
              </div>
            );
          }

          return (
            <Button
              type="dashed"
              onClick={() => handleOpenDiscountModal(record, editedRecord)}
            >
              Add Discount
            </Button>
          );
        }

        const discount = Number(record?.discount) || 0;
        const additional = Number(record?.additional) || 0;
        const isPending = record?.discountStatus === "pending";
        const isApproved = record?.discountStatus === "approved";
        const pendingAmount = Number(record?.tempDiscountAmount) || 0;

        return (
          <div className="flex flex-col gap-1 justify-center items-center text-center">
            {discount > 0 && (
              <div
                style={{ color: "#262626" }}
                className="text-sm font-semibold"
              >
                {discount.toLocaleString()}
              </div>
            )}
            {additional > 0 && (
              <div
                style={{ color: "#262626" }}
                className="text-sm font-semibold"
              >
                Additional: {additional.toLocaleString()}
              </div>
            )}
            {isPending && !record.tempMiscFeeType && (
              <Tag
                color="orange"
                className="font-semibold text-center m-0 w-fit mx-auto"
              >
                Pending: ₹{pendingAmount.toLocaleString()}
              </Tag>
            )}
            {isApproved && discount > 0 && (
              <Tag color="green" style={{ margin: 0 }}>
                Approved
              </Tag>
            )}
            {!discount && !additional && !isPending && !(isApproved && discount > 0) && (
              <div style={{ color: "#262626" }} className="text-sm">
                -
              </div>
            )}
          </div>
        );
      },
    },
    ...(isParent
      ? []
      : [
          {
            title: "Due Date",
            dataIndex: [],
            key: "",
            align: "center",
            render: (record) => {
              const isEditing = editingKey === record._id;

              return isEditing ? (
                <DatePicker
                  defaultValue={
                    record?.due_date ? moment(record.due_date) : null
                  }
                  format="DD/MM/YYYY"
                  onChange={(date) => {
                    const newStudentFee = editableStudentFee.map((item) =>
                      item._id === record._id
                        ? { ...item, due_date: date ? date.toISOString() : null }
                        : item,
                    );
                    setEditableStudentFee(newStudentFee);
                  }}
                />
              ) : (
                <div
                  key={record?._id}
                  style={{ color: "#111111" }}
                  className="text-center capitalize"
                >
                  {moment(record?.due_date).format("Do MMM YYYY")}
                </div>
              );
            },
          },
        ]),
    {
      title: "Last Paid",
      dataIndex: [],
      key: "",
      align: "center",
      render: (record) => (
        <div
          key={record?._id}
          style={{ color: "#111111" }}
          className="text-center capitalize"
        >
          {record?.last_paid
            ? moment(record?.last_paid).format("Do MMM YYYY")
            : ""}
        </div>
      ),
    },
  ];
  if (!isParent) {
    feeColumn.push({
      title: "Action",
      key: "action",
      align: "center",
      render: (record) => {
        if (record?.__isMiscRow) {
          return miscEditMode ? (
            <>
              <Button
                type="primary"
                onClick={() => setMiscEditMode(false)}
                style={{ marginRight: 8 }}
              >
                Save
              </Button>
              <Button onClick={() => setMiscEditMode(false)}>Cancel</Button>
            </>
          ) : (
            <Button
              type="link"
              onClick={() => {
                setEditingKey(""); // cancel any other row's edit
                setMiscEditMode(true);
              }}
            >
              Edit
            </Button>
          );
        }

        const isEditing = editingKey === record._id;

        return isEditing ? (
          <>
            <Button
              type="primary"
              onClick={() => saveEdit(record)}
              style={{ marginRight: 8 }}
            >
              Save
            </Button>
            <Button
              onClick={() => {
                cancelEditing();
                setMiscEditMode(false);
              }}
            >
              Cancel
            </Button>
          </>
        ) : (
          <Button
            type="link"
            onClick={() => {
              setMiscEditMode(false);
              startEditing(record);
            }}
          >
            Edit
          </Button>
        );
      },
    });
  }

  const fetchStudentFees = useCallback(async () => {
    if (!classId || !studentId) {
      message.error("Class ID and Student ID are required");
      return;
    }

    const queryParams = new URLSearchParams(window.location.search);
    const defaultYear = (() => {
      const m = new Date().getMonth() + 1;
      const y = new Date().getFullYear();
      const startYear = m >= 4 ? y : y - 1;
      return `${startYear}-${String(startYear + 1).slice(-2)}`;
    })();
    const selectedYear = queryParams.get("year") || defaultYear;

    try {
      const response = await axios({
        method: "get",
        url: `${serverUrl}/admin/fee-template/class/${classId}/student/${studentId}`,
        params: {
          year: selectedYear,
        },
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      });
      if (response.data?.result) {
        setStudentFee(response.data.result);
      } else {
        message.info("No fee data found");
        setStudentFee([]);
      }
    } catch (err) {
      message.error("Failed to fetch student fees");
    }
  }, [classId, studentId]);

  useEffect(() => {
    if (classId && studentId) {
      fetchStudentFees();
    }
  }, [classId, studentId, refreshKey, fetchStudentFees]);

  const startEditing = (record) => {
    setEditingKey(record._id);
    setEditableStudentFee(
      studentFee.map((item) =>
        item._id === record._id
          ? {
              ...item,
              discountAmount:
                item.discountStatus === "pending"
                  ? item.tempDiscountAmount || 0
                  : item.discount || 0,
              discountReason:
                item.discountStatus === "pending"
                  ? item.tempDiscountReason || ""
                  : item.reason || "",
              discountTermIndices:
                item.discountStatus === "pending"
                  ? item.tempDiscountTermIndices || null
                  : item.discountTermIndices || null,
            }
          : item,
      ),
    );
  };
  const cancelEditing = () => {
    setEditingKey("");
  };
  const saveEdit = async (record) => {
    try {
      const editedRecord = editableStudentFee.find(
        (item) => item._id === record._id,
      );

      const discountAmt = Number(editedRecord?.discountAmount || 0);
      const discountRsn = editedRecord?.discountReason || "";
      const termIndices = editedRecord?.discountTermIndices;

      if (discountAmt > 0 && !discountRsn.trim()) {
        message.error("A reason is required to apply the discount.");
        return;
      }

      // Don't send discountAmount when an approved discount already exists — the
      // backend guard blocks any re-submission for approved records. The user
      // may only be changing the due date, not the discount.
      const discountAlreadyApproved = editedRecord?.discountStatus === "approved";

      const updatePayload = {
        due_date: editedRecord?.due_date,
        ...(!discountAlreadyApproved && discountAmt > 0 && {
          discountAmount: discountAmt,
          discountReason: discountRsn.trim(),
          discountTermIndices: termIndices || null,
        }),
      };

      await axios.patch(
        `${serverUrl}/admin/student-fee/${record._id}`,
        updatePayload,
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        },
      );

      await fetchStudentFees();
      if (fetchStudentFee) {
        await fetchStudentFee();
      }

      setEditingKey("");
      message.success("Fee details updated successfully");
    } catch (error) {
      console.error("Save edit error:", error);
      message.error(
        error?.response?.data?.error?.errors?.[0] ||
          error?.response?.data?.message ||
          "Failed to update fee details",
      );
    }
  };
  const handleStudentFeeEdit = (record, dataIndex) => {
    setEditingStudentFeeRow({
      dataIndex: dataIndex,
      recordId: record._id,
    });

    const pc = Array.isArray(record.payable_components)
      ? record.payable_components[0]
      : record.payable_components;
    setEditedStudentFeeValues({
      ...editedStudentFeeValues,
      [record._id]: { ...(pc || {}) },
    });
  };

  function disabledDate(current) {
    return current && current < moment().startOf("day");
  }

  const getDynamicColumns = (data, dataIndex) => {
    const columns = [
      {
        title: "Name",
        key: "name",
        align: "center",
        render: (record) => {
          if (isEditMode) {
            return (
              <Input
                value={editedValues[record._id]?.name}
                onChange={(e) =>
                  setEditedValues({
                    ...editedValues,
                    [record._id]: {
                      ...editedValues[record._id],
                      name: e.target.value,
                    },
                  })
                }
              />
            );
          } else {
            return <span>{record?.name}</span>;
          }
        },
      },
      {
        title: "Fee",
        dataIndex: "",
        key: "tution",
        align: "center",
        render: (record) => {
          if (
            editingStudentFeeRow?.recordId === record._id &&
            editingStudentFeeRow?.dataIndex === dataIndex
          ) {
            return (
              <Input
                value={editedStudentFeeValues[record._id]?.tution || ""}
                onChange={(e) =>
                  setEditedStudentFeeValues({
                    ...editedStudentFeeValues,
                    [record._id]: {
                      ...editedStudentFeeValues[record._id],
                      tution: e.target.value,
                    },
                  })
                }
                onKeyDown={(e) => {
                  if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                    e.preventDefault();
                  }
                }}
                onWheel={(e) => e.target.blur()}
                type="number"
              />
            );
          } else if (isEditMode) {
            return (
              <Input
                type="number"
                value={editedValues[record._id]?.tution || ""}
                onChange={(e) =>
                  setEditedValues({
                    ...editedValues,
                    [record._id]: {
                      ...editedValues[record._id],
                      tution: e.target.value,
                    },
                  })
                }
                onKeyDown={(e) => {
                  if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                    e.preventDefault();
                  }
                }}
                onWheel={(e) => e.target.blur()}
              />
            );
          } else {
            return <span>{record?.payable_components[0]?.total_amount}</span>;
          }
        },
      },
      // {
      //   title: "Total Amount",
      //   dataIndex: [],
      //   key: "total_amount",
      //   align: "center",
      //   render: (record) => (
      //     <div style={{ color: "#616161" }} className=" text-base text-center">
      //       {record?.payable_components[0]?.total_amount}
      //     </div>
      //   ),
      // },
      {
        title: "Discount",
        key: "discount",
        align: "center",
        render: (record) => (
          <div style={{ color: "#ff4d4f" }} className="text-base text-center">
            ₹{(record?.discount || 0).toLocaleString()}
          </div>
        ),
      },
      {
        title: "Paid Amount",
        dataIndex: [],
        key: "paid",
        align: "center",
        render: (record) => (
          <div style={{ color: "#616161" }} className=" text-base text-center">
            {record?.paid || 0}
          </div>
        ),
      },
      {
        title: "Remaining Amount",
        dataIndex: [],
        key: "remaining",
        align: "center",
        render: (record) => (
          <div style={{ color: "#616161" }} className=" text-base text-center">
            {record?.remaining || 0}
          </div>
        ),
      },
      {
        title: "Status",
        key: "status",
        align: "center",
        render: (record) => {
          let statusColor = "red";
          let statusText = "Pending";
          const paid = record?.paid || 0;
          const remaining = record?.remaining || 0;
          const total = record?.payable_components[0]?.total_amount || 0;

          if (paid > 0 && remaining === 0) {
            statusColor = "green";
            statusText = "Paid";
          } else if (paid > 0 && remaining > 0) {
            statusColor = "orange";
            statusText = "Partial";
          }

          return (
            <div
              style={{
                color: statusColor,
                fontWeight: "bold",
                border: `1px solid ${statusColor}`,
                padding: "2px 8px",
                borderRadius: "4px",
                display: "inline-block",
              }}
            >
              {statusText}
            </div>
          );
        },
      },
      {
        title: "Due Date",
        key: "due_date",
        align: "center",
        render: (record) => {
          if (isEditMode) {
            return (
              <DatePicker
                format={"DD/MM/YYYY"}
                value={
                  editedValues[record._id]?.dueDate
                    ? moment(editedValues[record._id].dueDate)
                    : null
                }
                disabledDate={disabledDate}
                getPopupContainer={(trigger) => trigger.parentNode}
                onChange={(date) =>
                  setEditedValues({
                    ...editedValues,
                    [record._id]: {
                      ...editedValues[record._id],
                      dueDate: date,
                    },
                  })
                }
              />
            );
          } else {
            return (
              <span>{moment(record?.due_date).format("Do MMM YYYY")}</span>
            );
          }
        },
      },
    ];

    return columns;
  };

  const getExtraFeesData = (installmentList) => {
    if (!installmentList || installmentList.length === 0) return [];
    const otherFeeInst = installmentList.find(
      (item) => item.name?.toLowerCase() === "other fee",
    );
    if (!otherFeeInst) return [];
    const payableComponents =
      otherFeeInst?.payable_components?.[0] || otherFeeInst?.payable_components || {};

    const extraFees = [];
    const excluded = ["tution", "total_amount", "other"];

    Object.entries(payableComponents).forEach(([key, value]) => {
      if (!excluded.includes(key) && Number(value) > 0) {
        extraFees.push({
          key,
          name: key.charAt(0).toUpperCase() + key.slice(1),
          amount: Number(value),
        });
      }
    });
    return extraFees;
  };

  return (
    <>
      <div className="mt-8">
        <div
          style={{ borderRadius: "10px", color: "#616161" }}
          className=" bg-white drop-shadow-md w-full "
        >
          <div className="p-4">
            <h3 className="text-base font-semibold text-center pb-4 capitalize">
              {"Fee Summary"}
            </h3>
            {(() => {
              const parts = [];

              const hasApprovedTuitionDiscount = studentFee?.some(
                (sf) =>
                  sf?.fee_template?.type?.toLowerCase() === "tution" &&
                  sf?.discountStatus === "approved" &&
                  Number(sf?.discount || 0) > 0,
              );
              if (hasApprovedTuitionDiscount) parts.push("tuition");

              const hasApprovedTransportDiscount = studentFee?.some(
                (sf) =>
                  sf?.fee_template?.type?.toLowerCase() === "transportation" &&
                  sf?.discountStatus === "approved" &&
                  Number(sf?.discount || 0) > 0,
              );
              if (hasApprovedTransportDiscount) parts.push("transportation");

              const hasApprovedAccommodationDiscount = studentFee?.some(
                (sf) =>
                  sf?.fee_template?.type?.toLowerCase() === "accommodation" &&
                  sf?.discountStatus === "approved" &&
                  Number(sf?.discount || 0) > 0,
              );
              if (hasApprovedAccommodationDiscount) parts.push("accommodation");

              const hasMiscDiscount = studentFee?.some((sf) =>
                sf?.installment_list?.some(
                  (inst) =>
                    inst.name?.toLowerCase() === "other fee" &&
                    Number(inst.discount || 0) > 0,
                ),
              );
              if (hasMiscDiscount) parts.push("miscellaneous");

              if (parts.length === 0) return null;
              return (
                <Alert
                  type="success"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="Discount Approved"
                  description={`A ${parts.join(" and ")} fee discount has been approved for this student. Approved discounts are final and cannot be modified.`}
                />
              );
            })()}
            <Table
              rowKey="_id"
              columns={feeColumn}
              expandable={{
                rowExpandable: (record) => {
                  const isTuitionLike =
                    record?.fee_template?.type?.toLowerCase() === "tution" ||
                    record?.fee_template?.type?.toLowerCase() === "daycare";
                  if (!isTuitionLike) return false;
                  const comps = new Set();
                  (record?.installment_list || [])
                    .filter((inst) => inst.name?.toLowerCase() !== "other fee")
                    .forEach((inst) => {
                      const pc = inst.payable_components?.[0] || {};
                      Object.keys(pc).forEach((k) => { if (k !== "total_amount") comps.add(k); });
                    });
                  return comps.size > 1;
                },
                expandedRowRender: (record) => {
                  const comps = {};
                  (record?.installment_list || [])
                    .filter((inst) => inst.name?.toLowerCase() !== "other fee")
                    .forEach((inst) => {
                      const pc = inst.payable_components?.[0] || {};
                      Object.keys(pc).forEach((k) => {
                        if (k !== "total_amount") comps[k] = (comps[k] || 0) + Number(pc[k] || 0);
                      });
                    });
                  return (
                    <div style={{ padding: "8px 16px", display: "flex", flexWrap: "wrap", gap: 12 }}>
                      {Object.entries(comps).map(([k, v]) => (
                        <div key={k} style={{ fontSize: 12, color: "#555", background: "#f5f5f5", borderRadius: 6, padding: "4px 10px" }}>
                          <span style={{ fontWeight: 500 }}>{k.toLowerCase() === "tution" ? "Tuition" : k}</span>
                          <span style={{ color: "#1890ff", marginLeft: 6 }}>₹{Number(v).toLocaleString("en-IN")}</span>
                        </div>
                      ))}
                    </div>
                  );
                },
                expandIcon: ({ expanded, onExpand, record, expandable: canExpand }) =>
                  canExpand ? (
                    <span
                      onClick={(e) => onExpand(record, e)}
                      style={{
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 18,
                        height: 18,
                        borderRadius: 4,
                        border: "1px solid #d9d9d9",
                        fontSize: 14,
                        lineHeight: 1,
                        color: "#555",
                        userSelect: "none",
                      }}
                    >
                      {expanded ? "−" : "+"}
                    </span>
                  ) : null,
              }}
              dataSource={(() => {
                if (!studentFee || studentFee.length === 0) return [];
                const tuitionRec = studentFee.find(
                  (sf) => sf?.fee_template?.type?.toLowerCase() === "tution",
                );
                const otherFeeInst = tuitionRec?.installment_list?.find(
                  (i) => i.name?.toLowerCase() === "other fee",
                );
                const miscTotal = otherFeeInst
                  ? Number(
                      otherFeeInst.payable_components?.[0]?.total_amount ||
                        otherFeeInst.payable_components?.total_amount ||
                        0,
                    )
                  : 0;
                const rows = [...studentFee];
                if (otherFeeInst && miscTotal > 0) {
                  const isMiscPending = tuitionRec?.tempMiscFeeType;
                  rows.push({
                    _id: `${tuitionRec._id}-misc`,
                    __isMiscRow: true,
                    fee_template: { type: "Miscellaneous Fees" },
                    total: miscTotal - (otherFeeInst?.discount || 0),
                    paid: otherFeeInst?.paid || 0,
                    remaining: otherFeeInst?.remaining || 0,
                    discount: otherFeeInst?.discount || 0,
                    additional: 0,
                    miscDiscount: otherFeeInst?.discount || 0,
                    miscDiscountReason: otherFeeInst?.miscDiscountReason || "",
                    due_date: otherFeeInst?.due_date || tuitionRec?.due_date,
                    last_paid: (otherFeeInst?.paid > 0) ? (otherFeeInst?.last_paid || tuitionRec?.last_paid || null) : null,
                    discountStatus: isMiscPending
                      ? tuitionRec.discountStatus
                      : "none",
                    tempDiscountAmount: isMiscPending
                      ? tuitionRec.tempDiscountAmount
                      : 0,
                    tempDiscountReason: isMiscPending
                      ? tuitionRec.tempDiscountReason
                      : "",
                  });
                }
                return rows;
              })()}
              pagination={false}
              loading={studentFee?.isLoading}
              summary={(pageData) => {
                if (!pageData || pageData.length === 0) return null;

                const totalAmount = pageData.reduce((sum, record) => {
                  let displayedTotal;
                  if (record?.fee_template?.type?.toLowerCase() === "tution") {
                    displayedTotal = (record?.installment_list || [])
                      .filter((inst) => inst.name?.toLowerCase() !== "other fee")
                      .reduce((s, inst) => {
                        const comp =
                          inst.payable_components?.[0] ||
                          inst.payable_components ||
                          {};
                        return s + (Number(comp.total_amount) || 0);
                      }, 0);
                  } else {
                    displayedTotal =
                      Number(record?.total || 0) +
                      Number(record?.discount || 0) -
                      Number(record?.additional || 0);
                  }
                  return sum + displayedTotal;
                }, 0);
                const paidAmount = pageData.reduce((sum, record) => {
                  const otherFeeInst = record?.installment_list?.find(
                    (inst) => inst.name?.toLowerCase() === "other fee",
                  );
                  const otherPaid = otherFeeInst?.paid || 0;
                  const displayedPaid =
                    record?.fee_template?.type?.toLowerCase() === "tution"
                      ? (record?.paid || 0) - otherPaid
                      : record?.paid || 0;
                  return sum + displayedPaid;
                }, 0);
                const remainingAmount = pageData.reduce((sum, record) => {
                  const otherFeeInst = record?.installment_list?.find(
                    (inst) => inst.name?.toLowerCase() === "other fee",
                  );
                  const otherRemaining = otherFeeInst?.remaining || 0;
                  const displayedRemaining =
                    record?.fee_template?.type?.toLowerCase() === "tution"
                      ? (record?.remaining || 0) - otherRemaining
                      : record?.remaining || 0;
                  return sum + displayedRemaining;
                }, 0);
                const totalDiscount = pageData.reduce(
                  (sum, record) => sum + (Number(record?.discount) || 0),
                  0,
                );
                const totalAdditional = pageData.reduce(
                  (sum, record) => sum + (Number(record?.additional) || 0),
                  0,
                );

                return (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} />
                    <Table.Summary.Cell index={1} align="center">
                      <strong>Total</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="">
                      <strong>{totalAmount.toLocaleString()}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="">
                      <strong>{paidAmount.toLocaleString()}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="">
                      <strong>{remainingAmount.toLocaleString()}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="">
                      <strong>
                        {totalDiscount > 0 &&
                          `Discount: ${totalDiscount.toLocaleString()}`}
                        {totalDiscount > 0 && totalAdditional > 0 && " / "}
                        {totalAdditional > 0 &&
                          `Additional: ${totalAdditional.toLocaleString()}`}
                        {totalDiscount === 0 && totalAdditional === 0 && "-"}
                      </strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={6}
                      colSpan={feeColumn.length - 5}
                    ></Table.Summary.Cell>
                  </Table.Summary.Row>
                );
              }}
            />

            {!editingKey &&
              studentFee?.map((record) => {
                const discount = Number(record?.discount || 0);
                const additional = Number(record?.additional || 0);
                const reason = record?.reason;
                const isPending = record?.discountStatus === "pending";
                const tempDiscount = Number(record?.tempDiscountAmount || 0);
                const tempReason = record?.tempDiscountReason;

                const banners = [];

                const otherFeeInst = record?.installment_list?.find(
                  (i) => i.name?.toLowerCase() === "other fee",
                );
                const miscDiscount = Number(otherFeeInst?.discount || 0);
                const miscReason = otherFeeInst?.miscDiscountReason;

                if (miscDiscount > 0 && miscReason) {
                  banners.push(
                    <div
                      key={`${record._id}-misc-active`}
                      style={{
                        marginTop: "12px",
                        padding: "10px 16px",
                        borderRadius: "6px",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "10px",
                        border: "1px solid #ffccc7",
                        backgroundColor: "#fff2f0",
                      }}
                    >
                      <span style={{ fontSize: "16px", marginTop: "1px" }}>
                        🏷️
                      </span>
                      <div>
                        <div
                          style={{
                            fontWeight: "600",
                            fontSize: "13px",
                            color: "#cf1322",
                            marginBottom: "2px",
                          }}
                        >
                          Miscellaneous Fees — Discount of ₹
                          {miscDiscount.toLocaleString()} applied
                        </div>
                        {miscReason && (
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#cf1322",
                              fontStyle: "italic",
                              marginTop: "2px",
                            }}
                          >
                            Reason: {miscReason}
                          </div>
                        )}
                      </div>
                    </div>,
                  );
                }

                if ((discount > 0 || additional > 0) && reason) {
                  banners.push(
                    <div
                      key={`${record._id}-active`}
                      style={{
                        marginTop: "12px",
                        padding: "10px 16px",
                        borderRadius: "6px",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "10px",
                        border:
                          discount > 0
                            ? "1px solid #ffccc7"
                            : "1px solid #bae0ff",
                        backgroundColor: discount > 0 ? "#fff2f0" : "#e6f4ff",
                      }}
                    >
                      <span style={{ fontSize: "16px", marginTop: "1px" }}>
                        {discount > 0 ? "🏷️" : "➕"}
                      </span>
                      <div>
                        <div
                          style={{
                            fontWeight: "600",
                            fontSize: "13px",
                            color: discount > 0 ? "#cf1322" : "#0958d9",
                            marginBottom: "2px",
                          }}
                        >
                          {record?.fee_template?.type
                            ? record.fee_template.type.toLowerCase() ===
                              "tution"
                              ? "Tuition"
                              : record.fee_template.type
                                  .charAt(0)
                                  .toUpperCase() +
                                record.fee_template.type.slice(1)
                            : "Fee"}{" "}
                          —{" "}
                          {discount > 0
                            ? `Discount of ₹${discount.toLocaleString()} applied`
                            : `Additional ₹${additional.toLocaleString()} added`}
                        </div>
                        <div style={{ fontSize: "12px", color: "#555" }}>
                          <strong>Reason:</strong> {reason}
                        </div>
                      </div>
                    </div>,
                  );
                }

                if (isPending && tempDiscount > 0) {
                  const label = record.tempMiscFeeType
                    ? `Miscellaneous Fees (${record.tempMiscFeeType.charAt(0).toUpperCase() + record.tempMiscFeeType.slice(1)})`
                    : record?.fee_template?.type
                      ? record.fee_template.type.toLowerCase() === "tution"
                        ? "Tuition"
                        : record.fee_template.type.charAt(0).toUpperCase() +
                          record.fee_template.type.slice(1)
                      : "Fee";
                  banners.push(
                    <div
                      key={`${record._id}-pending`}
                      style={{
                        marginTop: "12px",
                        padding: "10px 16px",
                        borderRadius: "6px",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "10px",
                        border: "1px solid #ffe7ba",
                        backgroundColor: "#fffbe6",
                      }}
                    >
                      <span style={{ fontSize: "16px", marginTop: "1px" }}>
                        ⏳
                      </span>
                      <div>
                        <div
                          style={{
                            fontWeight: "600",
                            fontSize: "13px",
                            color: "#d46b08",
                            marginBottom: "2px",
                          }}
                        >
                          {label} — Pending Approval: Discount of ₹
                          {tempDiscount.toLocaleString()}
                        </div>
                        {tempReason && (
                          <div style={{ fontSize: "12px", color: "#555" }}>
                            <strong>Proposed Reason:</strong> {tempReason}
                          </div>
                        )}
                      </div>
                    </div>,
                  );
                }

                const pendingTxs = record?.pendingTransactions || [];
                pendingTxs.forEach((tx) => {
                  const label = tx.feeCategory === "other"
                    ? "Miscellaneous Fees"
                    : record?.fee_template?.type
                      ? record.fee_template.type.toLowerCase() === "tution"
                        ? "Tuition"
                        : record.fee_template.type.charAt(0).toUpperCase() +
                          record.fee_template.type.slice(1)
                      : "Fee";
                  
                  banners.push(
                    <div
                      key={`${tx._id}-pending-payment`}
                      style={{
                        marginTop: "12px",
                        padding: "10px 16px",
                        borderRadius: "6px",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "10px",
                        border: "1px solid #ffe7ba",
                        backgroundColor: "#fffbe6",
                      }}
                    >
                      <span style={{ fontSize: "16px", marginTop: "1px" }}>
                        ⏳
                      </span>
                      <div>
                        <div
                          style={{
                            fontWeight: "600",
                            fontSize: "13px",
                            color: "#d46b08",
                            marginBottom: "2px",
                          }}
                        >
                          {label} — Pending Approval: Payment of ₹
                          {tx.amount.toLocaleString()} ({tx.type || "Cash"})
                        </div>
                        {tx.name && (
                          <div style={{ fontSize: "12px", color: "#555" }}>
                            <strong>Collected From:</strong> {tx.name}
                          </div>
                        )}
                      </div>
                    </div>,
                  );
                });

                return banners;
              })}
          </div>

          <div className="py-5">
            {feeResponse &&
              feeResponse.length > 0 &&
              feeResponse?.map((fee, index) => (
                <>
                  <div style={{ marginBottom: "70px" }}>
                    <h3 className="text-base font-semibold text-center pb-4 capitalize">
                      {fee?.fee_template?.type?.toLowerCase() === "tution"
                        ? "Term wise Tuition Fee Split"
                        : fee?.fee_template?.type}
                    </h3>
                    <div className="flex flex-col lg:flex-row gap-6 items-start">
                      <div className="flex-1 w-full">
                        <Table
                          key={index}
                          columns={getDynamicColumns(
                            fee?.installment_list || [],
                            index,
                          )}
                          dataSource={
                            fee?.installment_list
                              ? fee.installment_list.filter(
                                  (item) =>
                                    item.name?.toLowerCase() !== "other fee",
                                )
                              : []
                          }
                          pagination={false}
                          loading={fee?.installment_list?.isLoading}
                          summary={(pageData) => {
                            if (!pageData || pageData.length === 0) return null;

                            const columns = getDynamicColumns(
                              fee?.installment_list || [],
                              index,
                            );

                            return (
                              <Table.Summary.Row>
                                {columns.map((col, colIdx) => {
                                  let content = null;

                                  if (colIdx === 0) {
                                    content = <strong>Total</strong>;
                                  } else if (col.key === "tution") {
                                    const totalFee = pageData.reduce(
                                      (sum, item) =>
                                        sum +
                                        (Number(
                                          item?.payable_components?.[0]?.total_amount,
                                        ) || 0),
                                      0,
                                    );
                                    content = (
                                      <strong>
                                        {totalFee.toLocaleString()}
                                      </strong>
                                    );
                                  } else if (
                                    col.key === "total_amount" ||
                                    col.title === "Total Amount"
                                  ) {
                                    const totalAmount = pageData.reduce(
                                      (sum, item) =>
                                        sum +
                                        (Number(
                                          item?.payable_components?.[0]
                                            ?.total_amount,
                                        ) || 0),
                                      0,
                                    );
                                    content = (
                                      <strong>
                                        {totalAmount.toLocaleString()}
                                      </strong>
                                    );
                                  } else if (col.key === "discount") {
                                    const totalDiscount = pageData.reduce(
                                      (sum, item) =>
                                        sum + (Number(item?.discount) || 0),
                                      0,
                                    );
                                    content = (
                                      <strong>
                                        {totalDiscount > 0
                                          ? `₹${totalDiscount.toLocaleString()}`
                                          : "-"}
                                      </strong>
                                    );
                                  } else if (col.key === "paid") {
                                    const totalPaid = pageData.reduce(
                                      (sum, item) =>
                                        sum + (Number(item?.paid) || 0),
                                      0,
                                    );
                                    content = (
                                      <strong>
                                        {totalPaid.toLocaleString()}
                                      </strong>
                                    );
                                  } else if (col.key === "remaining") {
                                    const totalRemaining = pageData.reduce(
                                      (sum, item) =>
                                        sum + (Number(item?.remaining) || 0),
                                      0,
                                    );
                                    content = (
                                      <strong>
                                        {totalRemaining.toLocaleString()}
                                      </strong>
                                    );
                                  } else if (
                                    col.key &&
                                    col.key !== "status" &&
                                    col.key !== "due_date" &&
                                    col.key !== "action"
                                  ) {
                                    const totalDynamic = pageData.reduce(
                                      (sum, item) =>
                                        sum +
                                        (Number(
                                          item?.payable_components?.[0]?.[
                                            col.key
                                          ],
                                        ) || 0),
                                      0,
                                    );
                                    content = (
                                      <strong>
                                        {totalDynamic.toLocaleString()}
                                      </strong>
                                    );
                                  }

                                  return (
                                    <Table.Summary.Cell
                                      key={colIdx}
                                      index={colIdx}
                                      className="text-center"
                                    >
                                      {content}
                                    </Table.Summary.Cell>
                                  );
                                })}
                              </Table.Summary.Row>
                            );
                          }}
                        />
                      </div>
                      {(() => {
                        const extraFeesData = getExtraFeesData(
                          fee?.installment_list || [],
                        );
                        if (extraFeesData.length === 0) return null;

                        const otherFeeInst = fee?.installment_list?.find(
                          (item) => item.name?.toLowerCase() === "other fee",
                        );
                        const paidVal = otherFeeInst?.paid || 0;
                        const remainingVal = otherFeeInst?.remaining || 0;
                        const totalVal = extraFeesData.reduce(
                          (sum, item) => sum + item.amount,
                          0,
                        );

                        return (
                          <div className="w-full lg:w-125">
                            <h4 className="text-sm font-bold mb-4 text-orange-500 flex items-center">
                              <span className="mr-2">📋</span> Miscellaneous
                              Fees
                            </h4>
                            <Table
                              columns={[
                                {
                                  title: "Fee Components",
                                  dataIndex: "name",
                                  key: "name",
                                  align: "center",
                                  className: "font-medium text-gray-700",
                                },
                                {
                                  title: "Amount",
                                  dataIndex: "amount",
                                  key: "amount",
                                  align: "center",
                                  render: (amount) =>
                                    `₹${Number(amount).toLocaleString()}`,
                                  className: "font-semibold text-gray-900",
                                },
                                {
                                  title: "Discount",
                                  key: "discount",
                                  align: "center",
                                  render: (_, componentRec, idx) => {
                                    // Discount now applies to the total misc fee, not a specific component.
                                    // Show the full approved/pending discount only on the first row (span rest).
                                    const approvedDisc = Number(
                                      otherFeeInst?.discount || 0,
                                    );
                                    const tuitionRec = studentFee?.find(
                                      (sf) =>
                                        sf?.fee_template?.type?.toLowerCase() ===
                                        "tution",
                                    );
                                    const pendingDisc =
                                      tuitionRec?.discountStatus ===
                                        "pending" && tuitionRec?.tempMiscFeeType
                                        ? Number(
                                            tuitionRec?.tempDiscountAmount || 0,
                                          )
                                        : 0;

                                    if (idx !== 0) {
                                      return { props: { rowSpan: 0 } };
                                    }

                                    const obj = {
                                      props: { rowSpan: extraFeesData.length },
                                    };
                                    if (approvedDisc > 0) {
                                      obj.children = (
                                        <div
                                          style={{ color: "#ff4d4f" }}
                                          className="font-semibold text-center"
                                        >
                                          ₹{approvedDisc.toLocaleString()}
                                        </div>
                                      );
                                    } else if (pendingDisc > 0) {
                                      obj.children = (
                                        <div className="flex justify-center">
                                          <Tag
                                            color="orange"
                                            className="font-semibold m-0"
                                          >
                                            Pending: ₹
                                            {pendingDisc.toLocaleString()}
                                          </Tag>
                                        </div>
                                      );
                                    } else {
                                      obj.children = (
                                        <div
                                          style={{ color: "#616161" }}
                                          className="text-center"
                                        >
                                          -
                                        </div>
                                      );
                                    }
                                    return obj;
                                  },
                                },
                                {
                                  title: "Total",
                                  dataIndex: "total",
                                  key: "total",
                                  align: "center",
                                  render: (value, record, idx) => {
                                    const obj = {
                                      children: (
                                        <strong>
                                          ₹{totalVal.toLocaleString()}
                                        </strong>
                                      ),
                                      props: {},
                                    };
                                    if (idx === 0) {
                                      obj.props.rowSpan = extraFeesData.length;
                                    } else {
                                      obj.props.rowSpan = 0;
                                    }
                                    return obj;
                                  },
                                },
                                {
                                  title: "Paid Amount",
                                  dataIndex: "paid",
                                  key: "paid",
                                  align: "center",
                                  render: (value, record, idx) => {
                                    const obj = {
                                      children: (
                                        <strong className="text-green-600">
                                          ₹{Number(paidVal).toLocaleString()}
                                        </strong>
                                      ),
                                      props: {},
                                    };
                                    if (idx === 0) {
                                      obj.props.rowSpan = extraFeesData.length;
                                    } else {
                                      obj.props.rowSpan = 0;
                                    }
                                    return obj;
                                  },
                                },
                                {
                                  title: "Remaining Amount",
                                  dataIndex: "remaining",
                                  key: "remaining",
                                  align: "center",
                                  render: (value, record, idx) => {
                                    const obj = {
                                      children: (
                                        <strong className="text-red-600">
                                          ₹
                                          {Number(
                                            remainingVal,
                                          ).toLocaleString()}
                                        </strong>
                                      ),
                                      props: {},
                                    };
                                    if (idx === 0) {
                                      obj.props.rowSpan = extraFeesData.length;
                                    } else {
                                      obj.props.rowSpan = 0;
                                    }
                                    return obj;
                                  },
                                },
                              ]}
                              dataSource={extraFeesData}
                              pagination={false}
                              bordered
                              size="middle"
                            />
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </>
              ))}
          </div>
        </div>
      </div>

      <Modal
        title="Apply Discount"
        visible={isDiscountModalVisible}
        onOk={handleSaveDiscount}
        onCancel={() => setIsDiscountModalVisible(false)}
        okText="Apply"
        cancelText="Cancel"
        destroyOnClose
      >
        <div className="flex flex-col gap-4 py-2">
          <Alert
            type="warning"
            showIcon
            message="One-time operation"
            description="A discount can only be applied once per fee. Once submitted and approved by an admin, it cannot be changed or reversed."
            style={{ fontSize: 12 }}
          />
          <div>
            <label className="block text-sm font-semibold mb-2">
              Discount Amount
            </label>
            <Input
              type="number"
              min={0}
              placeholder="Enter discount amount"
              value={modalDiscountAmount}
              onChange={(e) => setModalDiscountAmount(e.target.value)}
              onWheel={(e) => e.target.blur()}
            />
          </div>
          {Number(modalDiscountAmount || 0) !== 0 && (
            <>
              <div>
                <label className="block text-sm font-semibold mb-2">
                  Reason
                </label>
                <Input
                  placeholder="Reason for discount"
                  value={modalDiscountReason}
                  onChange={(e) => setModalDiscountReason(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">
                  Installment / Term(s)
                </label>
                <Select
                  mode="multiple"
                  placeholder="Select Term(s)"
                  style={{ width: "100%" }}
                  value={modalDiscountTermIndices}
                  onChange={handleSelectChange}
                >
                  <Select.Option value="all">All Terms</Select.Option>
                  {modalInstallments?.map((inst, idx) => {
                    if (
                      modalFeeType === "tution" &&
                      inst.name?.toLowerCase() === "other fee"
                    )
                      return null;
                    return (
                      <Select.Option key={idx} value={idx}>
                        {inst.name}
                      </Select.Option>
                    );
                  })}
                </Select>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* ── Miscellaneous Fees Discount Modal ── */}
      <Modal
        title="Apply Miscellaneous Fees Discount"
        visible={isMiscDiscountModalVisible}
        onOk={handleSaveMiscDiscount}
        onCancel={() => setIsMiscDiscountModalVisible(false)}
        okText="Apply"
        cancelText="Cancel"
        destroyOnClose
      >
        <div className="flex flex-col gap-4 py-2">
          <Alert
            type="warning"
            showIcon
            message="One-time operation"
            description="A discount can only be applied once per fee. Once submitted and approved by an admin, it cannot be changed or reversed."
            style={{ fontSize: 12 }}
          />
          <div>
            <label className="block text-sm font-semibold mb-2">
              Discount Amount
            </label>
            <Input
              type="number"
              min={0}
              placeholder="Enter discount amount"
              value={miscDiscountAmount}
              onChange={(e) => setMiscDiscountAmount(e.target.value)}
              onWheel={(e) => e.target.blur()}
            />
          </div>
          {Number(miscDiscountAmount || 0) !== 0 && (
            <div>
              <label className="block text-sm font-semibold mb-2">Reason</label>
              <Input
                placeholder="Reason for discount"
                value={miscDiscountReason}
                onChange={(e) => setMiscDiscountReason(e.target.value)}
              />
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default StudentFeDetails;
